import { storage } from "@/utils/storage";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
export const TOKEN_KEY = "yuo_session_token";

async function authHeader() {
  const token = await storage.secureGet(TOKEN_KEY, "");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(path, init = {}, { auth = true, timeout = 20000 } = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...(init.headers || {}),
  };
  if (auth) Object.assign(headers, await authHeader());

  const url = `${BASE_URL}/api${path}`;
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeout);
  let res;
  try {
    res = await fetch(url, { ...init, headers, signal: controller.signal });
  } catch (e) {
    clearTimeout(t);
    if (e?.name === "AbortError") {
      throw { status: 0, message: "Request timed out. Please try again." };
    }
    throw { status: 0, message: e?.message || "Network error" };
  }
  clearTimeout(t);
  const text = await res.text();
  const data = text
    ? (() => {
        try {
          return JSON.parse(text);
        } catch {
          return text;
        }
      })()
    : null;
  if (!res.ok) {
    // Preserve the raw detail object for structured errors — e.g. 402
    // plan-gate payloads carry {type,feature,plan,next_plan,limit,used,message_*}.
    const detail = data && typeof data === "object" ? data.detail : null;
    const detailObj = detail && typeof detail === "object" ? detail : null;
    if (res.status === 402 && detailObj?.type === "plan_gate" && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("yuo:plan-gate", { detail: detailObj }));
    }
    const messageStr =
      (detailObj?.message_en || detailObj?.message_ru) ||
      (typeof detail === "string" ? detail : null) ||
      (data && data.message) ||
      res.statusText ||
      "Request failed";
    throw { status: res.status, message: String(messageStr), detail: detailObj };
  }
  return data;
}

export const api = {
  // Contribution (inline Stripe Elements)
  contributionConfig: () => request("/contribution/config", {}, { auth: false }),
  contributionIntent: (body) => request("/contribution/intent", { method: "POST", body: JSON.stringify(body) }),
  contributionConfirm: (body) => request("/contribution/confirm", { method: "POST", body: JSON.stringify(body) }),
  myContributions: () => request("/contribution/me"),

  // Symbiosis local translator
  translate: (text, targetLang) =>
    request("/translate", { method: "POST", body: JSON.stringify({ text, targetLang }) }, { timeout: 45000 }),

  // Auth
  createSession: (session_token) =>
    request(
      "/auth/session",
      { method: "POST", body: JSON.stringify({ session_token }) },
      { auth: false }
    ),
  me: () => request("/auth/me"),
  setTheme: (theme) => request("/me/theme", { method: "PUT", body: JSON.stringify({ theme }) }),
  logout: () => request("/auth/logout", { method: "POST" }),

  // Library of Meanings
  listBooks: () => request("/library/books"),
  selectBook: (book_id, lang = "ru") =>
    request("/library/select", {
      method: "POST",
      body: JSON.stringify({ book_id, lang }),
    }),
  listPassages: (book_id, lang = "ru", theme) =>
    request(
      `/library/passages?book_id=${encodeURIComponent(book_id)}&lang=${lang}${
        theme ? `&theme=${encodeURIComponent(theme)}` : ""
      }`
    ),
  libraryAttunement: (intent = "diary") =>
    request(`/library/attunement?intent=${intent}`),
  suggestBook: (data) =>
    request("/library/suggest-book", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // Full-text library (Bible + Marcus Aurelius) + Theologian AI + margin notes
  fullBooks: () => request("/library/full/books"),
  fullChapter: (source, code, chapter, lang = "ru") =>
    request(
      `/library/full/chapter?source=${encodeURIComponent(
        source
      )}&code=${encodeURIComponent(code)}&chapter=${chapter}&lang=${lang}`
    ),
  verseBreakdown: (data) =>
    request(
      "/library/verse/breakdown",
      { method: "POST", body: JSON.stringify({ ...data, lang: data.lang || "ru" }) },
      { timeout: 90000 }
    ),
  libraryLimits: () => request("/library/limits"),
  dailyTriad: (lang = "ru", date) =>
    request(
      `/library/daily-triad?lang=${lang}${date ? `&date=${date}` : ""}`
    ),
  listBreakdowns: () => request("/library/breakdowns"),
  getBreakdown: (id) => request(`/library/breakdowns/${id}`),
  listMarginNotes: () => request("/library/notes"),
  createMarginNote: (data) =>
    request("/library/notes", { method: "POST", body: JSON.stringify(data) }),
  updateMarginNote: (id, note) =>
    request(`/library/notes/${id}`, { method: "PUT", body: JSON.stringify({ note }) }),
  deleteMarginNote: (id) =>
    request(`/library/notes/${id}`, { method: "DELETE" }),

  // Screenwriter
  screenwriterFormat: (data) =>
    request(
      "/screenwriter/format",
      { method: "POST", body: JSON.stringify(data) },
      { timeout: 90000 }
    ),
  screenwriterImport: (data) =>
    request(
      "/screenwriter/import",
      { method: "POST", body: JSON.stringify(data) },
      { timeout: 90000 }
    ),
  screenwriterTransitions: (blocks) =>
    request(
      "/screenwriter/transitions",
      { method: "POST", body: JSON.stringify({ blocks }) },
      { timeout: 90000 }
    ),
  screenwriterTranslate: (data) =>
    request(
      "/screenwriter/translate",
      { method: "POST", body: JSON.stringify(data) },
      { timeout: 120000 }
    ),
  screenwriterSources: () => request("/screenwriter/sources"),
  screenwriterSourceMessages: (kind, id) =>
    request(`/screenwriter/sources/${kind}/${encodeURIComponent(id)}`),
  screenwriterListScripts: () => request("/screenwriter/scripts"),
  screenwriterCreateScript: (data) =>
    request("/screenwriter/scripts", { method: "POST", body: JSON.stringify(data) }),
  screenwriterUpdateScript: (id, data) =>
    request(`/screenwriter/scripts/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  screenwriterDeleteScript: (id) =>
    request(`/screenwriter/scripts/${id}`, { method: "DELETE" }),

  // Planning
  planningList: () => request("/planning/plans"),
  planningCreate: (data) =>
    request("/planning/plans", { method: "POST", body: JSON.stringify(data) }),
  planningUpdate: (id, data) =>
    request(`/planning/plans/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  planningDelete: (id) =>
    request(`/planning/plans/${id}`, { method: "DELETE" }),

  // Voice → text (Whisper)
  transcribeAudio: async ({ blob, filename, mime }) => {
    const url = `${BASE_URL}/api/transcribe`;
    if (!blob) throw { status: 0, message: "Nothing to transcribe" };
    const form = new FormData();
    const name = filename || "recording.webm";
    form.append("file", blob, name);
    const headers = { ...(await authHeader()) };
    // IMPORTANT: do NOT set Content-Type — the browser adds the multipart boundary.
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 90000);
    let res;
    try {
      res = await fetch(url, {
        method: "POST",
        headers,
        body: form,
        signal: controller.signal,
      });
    } catch (e) {
      clearTimeout(t);
      throw { status: 0, message: e?.message || "Network error" };
    }
    clearTimeout(t);
    const bodyText = await res.text();
    const data = bodyText
      ? (() => {
          try {
            return JSON.parse(bodyText);
          } catch {
            return bodyText;
          }
        })()
      : null;
    if (!res.ok) {
      const message =
        (data && (data.detail || data.message)) || res.statusText || "Transcription failed";
      throw { status: res.status, message: String(message) };
    }
    return data;
  },

  // Profile
  getProfile: () => request("/profile"),
  updateProfile: (data) =>
    request("/profile", { method: "PUT", body: JSON.stringify(data) }),
  introspectProfile: () =>
    request(
      "/profile/introspect",
      { method: "POST" },
      { timeout: 90000 }
    ),

  // Conversations
  listConversations: (section) =>
    request(`/conversations${section ? `?section=${section}` : ""}`),
  createConversation: (section = "mirror") =>
    request(`/conversations?section=${section}`, { method: "POST" }),
  getConversation: (id) => request(`/conversations/${id}`),
  deleteConversation: (id) =>
    request(`/conversations/${id}`, { method: "DELETE" }),
  sendMessage: (id, content, attachments, opts) =>
    request(
      `/chat/${id}/message`,
      {
        method: "POST",
        body: JSON.stringify({
          content,
          attachments,
          via_voice: opts?.via_voice || false,
          voice_language: opts?.voice_language || null,
        }),
      },
      { timeout: 90000 }
    ),
  contentMirrorDiary: () =>
    request(`/content/mirror-diary`, { method: "POST" }, { timeout: 120000 }),

  // Diary
  listDiary: () => request("/diary"),
  createDiary: (data) =>
    request("/diary", { method: "POST", body: JSON.stringify(data) }),
  getDiary: (id) => request(`/diary/${id}`),
  updateDiary: (id, data) =>
    request(`/diary/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteDiary: (id) => request(`/diary/${id}`, { method: "DELETE" }),
  reflectDiary: (id) =>
    request(`/diary/${id}/reflect`, { method: "POST" }, { timeout: 90000 }),
  deleteDiaryReflection: (entryId, reflectionId) =>
    request(`/diary/${entryId}/reflections/${reflectionId}`, { method: "DELETE" }),

  // Symbiosis
  listShared: () => request("/symbiosis"),
  shareConversation: (conversation_id) =>
    request(`/symbiosis/from-conversation/${conversation_id}`, { method: "POST" }),
  createShared: (data) =>
    request("/symbiosis", { method: "POST", body: JSON.stringify(data) }),
  deleteShared: (id) => request(`/symbiosis/${id}`, { method: "DELETE" }),
  publicShareUrl: (share_id) =>
    `${BASE_URL}/api/symbiosis/public/${share_id}`,

  // Symbiosis rooms
  createRoom: (title) =>
    request("/symbiosis/room", {
      method: "POST",
      body: JSON.stringify({ title: title || null }),
    }),
  listRooms: () => request("/symbiosis/rooms"),
  getRoom: (share_id) => request(`/symbiosis/room/${share_id}`),
  renameRoom: (share_id, title) =>
    request(`/symbiosis/room/${share_id}/rename`, { method: "POST", body: JSON.stringify({ title }) }),
  deleteRoom: (share_id) =>
    request(`/symbiosis/room/${share_id}`, { method: "DELETE" }),
  deleteRoomAction: (share_id, action) =>
    request(`/symbiosis/room/${share_id}/delete`, {
      method: "POST",
      body: JSON.stringify({ action }),
    }),
  roomWebSocketUrl: (share_id, name) => {
    const base = (BASE_URL || "").replace(/^http/, "ws");
    const qp = new URLSearchParams({ name }).toString();
    return `${base}/api/ws/room/${share_id}?${qp}`;
  },
  roomShareUrl: (share_id) => `${window.location.origin}/symbiosis/room/${share_id}`,
  inviteRoomByEmail: (share_id, email) =>
    request(`/symbiosis/room/${share_id}/invite`, {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  myInvitations: () => request("/symbiosis/invitations"),

  // Billing
  createCheckout: (origin_url, plan = "pro", period = "monthly") =>
    request("/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ origin_url, plan, period }),
    }),
  personalizationQuestions: () => request("/personalization/questions"),
  personalizationAnswer: (question_id, option_id) =>
    request("/personalization/answer", {
      method: "POST",
      body: JSON.stringify({ question_id, option_id }),
    }),
  personalizationFinalize: () =>
    request("/personalization/finalize", { method: "POST" }),

  // YUO Personalization (Conscious Upgrade / Growth)
  upgradeConfig: () => request("/personalization/upgrade/config"),
  // Claude Sonnet often takes 25–40s to produce the full 10-type + IQ analysis,
  // so we allow a generous 90s timeout — otherwise the submit "errors out" with a
  // false timeout right before the AI response arrives.
  upgradeSubmit: ({ body_form_id, answers, lang }) =>
    request("/personalization/upgrade/submit", {
      method: "POST",
      body: JSON.stringify({ body_form_id, answers, lang }),
    }, { timeout: 90000 }),

  // Diary Insights
  listInsights: () => request("/diary/insights"),
  generateInsight: ({ lang } = {}) =>
    request("/diary/insights/generate", {
      method: "POST",
      body: JSON.stringify({ lang: lang || "en" }),
    }),
  updateInsightNote: (insight_id, note) =>
    request(`/diary/insights/${insight_id}/note`, {
      method: "PATCH",
      body: JSON.stringify({ note }),
    }),
  billingStatus: (session_id) => request(`/billing/status/${session_id}`),
  billingChange: (plan, period) =>
    request("/billing/change", { method: "POST", body: JSON.stringify({ plan, period }) }),
};
